import { Notice } from '@verlo/shared/components/ui/notice';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@verlo/shared/components/ui/dialog';
import { Button } from '@verlo/shared/components/ui/button';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import { Input } from '@verlo/shared/components/ui/input';
import { CheckCircle, Plus, Minus, Mail, Printer, ShieldAlert } from 'lucide-react';
import { receiptsApi, printingApi } from '@verlo/shared/services/api';
import { toast } from 'sonner';
import { useEffect, useRef, useState } from 'react';
import { splitShare, guestsLeft, saveSplit, clearSplit, resumeSplit } from '../../lib/splitBill';
import RefundDialog from './RefundDialog';
import CashTendered, { cashProblem, changeFor } from './CashTendered';
import { OptionChips } from '@verlo/shared/components/ui/segmented';
import { useDrawer } from '../../contexts/DrawerContext';

// Three steps a server can predict (FOH plan round two, G): method → tip → confirm. Tip
// is offered on card and cash alike (Martin, 15 Sep) and has to come before "take it on
// the machine", since the machine takes the total. Split stays as it was, entered from
// the method step.
const TIP_PRESETS = [
  { value: 0, label: 'None' },
  { value: 10, label: '10%' },
  { value: 12.5, label: '12.5%' },
  { value: 15, label: '15%' },
  { value: 'other', label: 'Other' },
];

const PaymentDialog = ({
  open,
  onOpenChange,
  paymentStep,
  paymentMethod = 'card',
  onPaymentMethodChange,
  splitBillMode,
  splitType,
  splitPeople,
  customAmount,
  orderPayments,
  remainingBalance,
  total,
  tipAmount,
  onTipChange,
  orderId,
  onPaymentStepChange,
  onSplitBillModeChange,
  onSplitTypeChange,
  onSplitPeopleChange,
  onCustomAmountChange,
  onPaymentSuccess,
  onPartialPaymentSuccess,
  onViewReceipt,
  // A paid table's next step: clear it. And the table's name, for "Clear T1" and the line under "Paid".
  onClearTable,
  tableLabel,
  formatCurrency,
  order,
  onRefunded
}) => {
  const [stage, setStage] = useState('method');   // method | tip | confirm, while paymentStep is 'waiting'
  // Cash: what the guest handed over (CashTendered), and the change the last payment gave.
  const [tendered, setTendered] = useState(null);
  const [lastChange, setLastChange] = useState(0);
  const [tipPreset, setTipPreset] = useState(0);
  const [tipOther, setTipOther] = useState('');
  // An even split is paid one guest at a time. Counting who has paid is what lets the next
  // guest be asked for their share of what is *left* (lib/splitBill.js has the long version).
  const [guestsPaid, setGuestsPaid] = useState(0);
  const splittingByPeopleRef = useRef(false);
  const splitKey = order?.id || orderId;
  useEffect(() => {
    if (!open) return;
    setStage('method'); setTipPreset(0); setTipOther(''); onTipChange?.(0); setTendered(null);
    // A split carries on from where it stopped. The count used to be reset here, so closing
    // the dialog between guests (or the till locking, which closes it for you) restarted the
    // split on what was left with the original head count.
    const kept = resumeSplit(splitKey, { payments: orderPayments.length, remaining: remainingBalance });
    setGuestsPaid(kept ? kept.paid : 0);
    if (kept) onSplitPeopleChange?.(kept.people);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  // Kept as it changes, so a lock mid-split loses nothing; forgotten once the bill is paid.
  useEffect(() => { saveSplit(splitKey, { people: splitPeople, paid: guestsPaid }); }, [splitKey, splitPeople, guestsPaid]);
  useEffect(() => {
    if (orderPayments.length > 0 && remainingBalance <= 0) clearSplit(splitKey);
  }, [splitKey, orderPayments.length, remainingBalance]);
  useEffect(() => {
    if (paymentStep === 'success' && splittingByPeopleRef.current) {
      splittingByPeopleRef.current = false;
      setGuestsPaid((n) => n + 1);
    }
    if (paymentStep === 'waiting') splittingByPeopleRef.current = false;
  }, [paymentStep]);
  // A terminal with no cash drawer (the floor tills at Yard & Coop) takes card only — the
  // server refuses cash there too. A cash payment opens the session, so the header word
  // is refreshed once it lands.
  const { hasDrawer, refresh: refreshDrawer } = useDrawer();
  useEffect(() => { if (open && !hasDrawer && paymentMethod === 'cash') onPaymentMethodChange?.('card'); }, [open, hasDrawer, paymentMethod]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (paymentStep === 'success' && paymentMethod === 'cash') refreshDrawer(); }, [paymentStep]); // eslint-disable-line react-hooks/exhaustive-deps
  const base = remainingBalance > 0 ? remainingBalance : Math.max(0, (total || 0) - (tipAmount || 0));
  const pickTip = (v) => {
    setTipPreset(v);
    if (v === 'other') { onTipChange?.(parseFloat(tipOther) || 0); return; }
    onTipChange?.(Math.round(base * (Number(v) / 100) * 100) / 100);
  };
  const toTake = base + (tipAmount || 0);
  // A split's tip is each guest's own, on their own share: the same step a whole bill has.
  // Reset for every guest; the tip they chose rides on their payment (`tip_amount`).
  const [splitTipPreset, setSplitTipPreset] = useState(0);
  const [splitTipOther, setSplitTipOther] = useState('');
  const [lastTip, setLastTip] = useState(0);
  const [receiptEmail, setReceiptEmail] = useState('');
  const [sendingReceipt, setSendingReceipt] = useState(false);
  const [receiptSent, setReceiptSent] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [printing, setPrinting] = useState(false);

  // A guest who wants paper asks for it; the kitchen's tickets print themselves (#94).
  const handlePrintReceipt = async () => {
    setPrinting(true);
    try {
      await printingApi.printReceipt(orderId);
      toast.success('Receipt printing');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not print the receipt');
    } finally {
      setPrinting(false);
    }
  };

  const handleSendReceipt = async () => {
    if (!receiptEmail || !receiptEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setSendingReceipt(true);
    try {
      const res = await receiptsApi.send(orderId, receiptEmail);
      // S4-7: the Node queues the receipt and the Cloud sends it, so the answer is "queued",
      // not "sent" — the receipt is on its way (and survives a WAN outage).
      if (res.data.status === 'queued' || res.data.status === 'sent') {
        toast.success(`Receipt to ${receiptEmail} is on its way`);
        setReceiptSent(true);
      } else {
        toast.info('Email service not configured yet. Receipt generated.');
        setReceiptSent(true);
      }
    } catch {
      toast.error('Failed to send receipt');
    } finally {
      setSendingReceipt(false);
    }
  };
  const owed = remainingBalance > 0 ? remainingBalance : total;
  const share = splitShare(owed, guestsLeft(splitPeople, guestsPaid));
  const splitBase = splitType === 'amount' ? (parseFloat(customAmount) || 0) : share;
  const pickSplitTip = (v) => {
    setSplitTipPreset(v);
    if (v === 'other') { onTipChange?.(Math.max(0, parseFloat(splitTipOther) || 0)); return; }
    onTipChange?.(Math.round(splitBase * (Number(v) / 100) * 100) / 100);
  };
  // A percentage follows the share: two guests becoming three changes what 10% is.
  useEffect(() => {
    if (splitBillMode && paymentStep === 'waiting' && splitTipPreset !== 'other') {
      onTipChange?.(Math.round(splitBase * (Number(splitTipPreset) / 100) * 100) / 100);
    }
  }, [splitBase]); // eslint-disable-line react-hooks/exhaustive-deps
  // Each guest starts with no tip.
  useEffect(() => {
    if (splitBillMode && paymentStep === 'waiting') {
      setSplitTipPreset(0);
      setSplitTipOther('');
      onTipChange?.(0);
    }
  }, [splitBillMode, paymentStep]); // eslint-disable-line react-hooks/exhaustive-deps
  // What the payment just taken came to, for "Paid · £40.70".
  const [lastPaid, setLastPaid] = useState(null);
  const cash = paymentMethod === 'cash';
  const splitToTake = splitBase + (tipAmount || 0);
  // Cash needs what was handed over, and at least what's being taken.
  // Nothing handed over yet, less than the bill, or more change than any note (CashTendered).
  const cashShort = (due) => cash && cashProblem(tendered, due) !== 'ok';
  const cashWords = (due) => (tendered != null && changeFor(tendered, due) > 0
    ? `Take ${formatCurrency(tendered)} · give ${formatCurrency(changeFor(tendered, due))} change`
    : `Take ${formatCurrency(due)} in cash`);
  const handlePartialPayment = () => {
    setLastPaid(splitToTake);
    setLastTip(tipAmount || 0);
    setLastChange(cash && tendered != null ? changeFor(tendered, splitToTake) : 0);
    const handed = cash ? tendered : undefined;
    setTendered(null);
    if (splitType === 'amount') {
      onPartialPaymentSuccess(parseFloat(customAmount), handed);
      return;
    }
    splittingByPeopleRef.current = true;
    onPartialPaymentSuccess(share, handed);
  };

  const handleFullPayment = () => {
    setLastPaid(toTake);
    setLastTip(tipAmount || 0);
    setLastChange(cash && tendered != null ? changeFor(tendered, toTake) : 0);
    onPaymentSuccess(cash ? tendered : undefined);
  };

  const MethodToggle = () => (
    <Segmented
      size="lg"
      stretch
      value={paymentMethod}
      onChange={(m) => onPaymentMethodChange?.(m)}
      options={[
        { value: 'card', label: 'Card', testId: 'payment-method-card' },
        ...(hasDrawer ? [{ value: 'cash', label: 'Cash', testId: 'payment-method-cash' }] : []),
      ]}
      data-testid="payment-method-toggle"
    />
  );

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) {
        onPaymentStepChange('waiting');
        onSplitBillModeChange(false);
      }
      onOpenChange(isOpen);
    }}>
      <DialogContent size="form" onPointerDownOutside={(e) => {
        if (paymentStep === 'success') e.preventDefault();
      }}>
        <DialogHeader className="text-center">
          <DialogTitle className="text-xl">
            {paymentStep === 'success'
              ? (remainingBalance > 0 ? 'Partial payment taken' : 'Payment taken')
              : splitBillMode ? 'Split the bill'
              : stage === 'tip' ? 'Add a tip?'
              : stage === 'confirm' ? 'Confirm'
              : 'Take payment'
            }
          </DialogTitle>
        </DialogHeader>
        <div className="py-6 text-center space-y-4">
          {paymentStep === 'waiting' && !splitBillMode && stage === 'method' && (
            <>
              <div>
                <p className="text-3xl font-bold tabular-nums" data-testid="payment-amount">{formatCurrency(base)}</p>
                {remainingBalance > 0 && orderPayments.length > 0 && (
                  <p className="text-sm text-signal-warn mt-1">Remaining after {formatCurrency(orderPayments.reduce((sum, p) => sum + (p.amount || 0), 0))} already taken</p>
                )}
              </div>
              <p className="text-[11px] uppercase tracking-wide text-ink-faint">How are they paying?</p>
              <MethodToggle />
            </>
          )}

          {paymentStep === 'waiting' && !splitBillMode && stage === 'tip' && (
            <>
              <p className="text-sm text-ink-soft">On {formatCurrency(base)}, {paymentMethod === 'cash' ? 'in cash' : 'on the card'}.</p>
              <OptionChips
                value={tipPreset}
                onChange={pickTip}
                options={TIP_PRESETS.map((t) => ({ value: t.value, label: t.label, testId: `tip-${String(t.value).replace('.', '_')}` }))}
                className="justify-center"
                chipClassName="h-11 min-w-[64px] text-[15px]"
              />
              {tipPreset === 'other' && (
                <div className="relative max-w-[200px] mx-auto">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft font-medium">£</span>
                  <Input
                    type="number" step="0.01" min="0" placeholder="0.00" autoFocus
                    value={tipOther}
                    onChange={(e) => { setTipOther(e.target.value); onTipChange?.(Math.max(0, parseFloat(e.target.value) || 0)); }}
                    className="pl-8 text-xl font-bold text-center h-12"
                    data-testid="tip-other-input"
                  />
                </div>
              )}
              <div className="rounded-lg bg-surface-2 p-3 text-sm">
                <div className="flex justify-between text-ink-soft"><span>Bill</span><span className="tabular-nums">{formatCurrency(base)}</span></div>
                <div className="flex justify-between text-ink-soft"><span>Tip</span><span className="tabular-nums">{formatCurrency(tipAmount || 0)}</span></div>
                <div className="flex justify-between font-bold pt-1 mt-1 border-t"><span>To take</span><span className="tabular-nums" data-testid="payment-to-take">{formatCurrency(toTake)}</span></div>
              </div>
            </>
          )}

          {paymentStep === 'waiting' && !splitBillMode && stage === 'confirm' && (
            <>
              <div className="rounded-lg bg-surface-2 p-3 text-left text-sm max-h-56 overflow-y-auto" data-testid="payment-summary">
                {(order?.items || []).filter((l) => !l.voided).map((l, i) => (
                  <div key={l.line_id || i} className="flex justify-between py-0.5">
                    <span className="text-ink-soft truncate mr-3"><span className="font-mono text-ink-faint">{l.quantity}×</span> {l.name}</span>
                    <span className={`tabular-nums shrink-0 ${l.comp ? 'text-signal-ok' : ''}`}>{formatCurrency(l.comp ? 0 : (l.subtotal ?? l.price * l.quantity))}</span>
                  </div>
                ))}
                {order?.discount_amount > 0 && (
                  <div className="flex justify-between py-0.5 text-signal-ok"><span>Discount</span><span className="tabular-nums">-{formatCurrency(order.discount_amount)}</span></div>
                )}
                {orderPayments.length > 0 && (
                  <div className="flex justify-between py-0.5 text-signal-ok"><span>Already paid</span><span className="tabular-nums">-{formatCurrency(orderPayments.reduce((sum, p) => sum + (p.amount || 0), 0))}</span></div>
                )}
                {tipAmount > 0 && (
                  <div className="flex justify-between py-0.5"><span>Tip</span><span className="tabular-nums">{formatCurrency(tipAmount)}</span></div>
                )}
                <div className="flex justify-between pt-2 mt-1 border-t text-base font-bold"><span>To take</span><span className="tabular-nums">{formatCurrency(toTake)}</span></div>
              </div>
              {cash ? (
                <CashTendered due={toTake} value={tendered} onChange={setTendered} formatCurrency={formatCurrency} />
              ) : (
                <p className="text-sm text-ink-soft font-medium">Take {formatCurrency(toTake)} on the card machine, then confirm.</p>
              )}
            </>
          )}
          
          {paymentStep === 'waiting' && splitBillMode && (
            <>
              <div className="bg-surface-2 rounded-lg p-4 text-left">
                <div className="flex justify-between mb-2">
                  <span className="text-ink-soft">Bill</span>
                  {/* The bill, not the bill and this guest's tip: `total` carries the tip being chosen. */}
                  <span className="font-bold">{formatCurrency(total - (tipAmount || 0))}</span>
                </div>
                {orderPayments.length > 0 && (
                  <div className="flex justify-between mb-2 text-signal-ok">
                    <span>Already paid</span>
                    <span className="font-bold">{formatCurrency(orderPayments.reduce((sum, p) => sum + (p.amount || 0), 0))}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t text-lg">
                  <span className="font-medium">Remaining</span>
                  <span className="font-bold text-verlo">{formatCurrency(remainingBalance > 0 ? remainingBalance : total - (tipAmount || 0))}</span>
                </div>
              </div>

              <MethodToggle />

              {/* How to split */}
              <Segmented
                stretch
                value={splitType}
                onChange={onSplitTypeChange}
                options={[
                  { value: 'amount', label: 'Custom amount' },
                  { value: 'people', label: 'Split by people' },
                ]}
              />
              
              {splitType === 'amount' ? (
                <div className="space-y-3">
                  <p className="text-sm text-ink-soft">Enter the amount this customer will pay:</p>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft font-medium">£</span>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={customAmount}
                      onChange={(e) => onCustomAmountChange(e.target.value)}
                      className="pl-8 text-2xl font-bold text-center h-14"
                      data-testid="custom-amount-input"
                    />
                  </div>
                  {customAmount && parseFloat(customAmount) > (remainingBalance > 0 ? remainingBalance : total) && (
                    <p className="text-sm text-signal-stop">Amount exceeds remaining balance</p>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-ink-soft">How many people are splitting?</p>
                  <div className="flex items-center justify-center gap-4">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-12 w-12"
                      onClick={() => onSplitPeopleChange(Math.max(2, guestsPaid + 1, splitPeople - 1))}
                      disabled={splitPeople <= Math.max(2, guestsPaid + 1)}
                    >
                      <Minus size={20} />
                    </Button>
                    <span className="text-4xl font-bold w-16 text-center" data-testid="split-people">{splitPeople}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-12 w-12"
                      onClick={() => onSplitPeopleChange(Math.min(20, splitPeople + 1))}
                      data-testid="split-people-plus"
                    >
                      <Plus size={20} />
                    </Button>
                  </div>
                  
                  {/* This guest's share, and where we are in the table. */}
                  <div className="bg-verlo rounded-xl p-4 text-white" data-testid="split-share">
                    <p className="text-sm opacity-80" data-testid="split-guest">
                      Guest {Math.min(guestsPaid + 1, splitPeople)} of {splitPeople} pays
                    </p>
                    <p className="text-3xl font-bold mt-1" data-testid="split-share-amount">{formatCurrency(share)}</p>
                  </div>
                </div>
              )}

              {/* This guest's tip, on this guest's share. */}
              {splitBase > 0 && (
                <div className="space-y-2" data-testid="split-tip">
                  <p className="text-sm font-medium text-ink">Add a tip?</p>
                  <OptionChips
                    value={splitTipPreset}
                    onChange={pickSplitTip}
                    options={TIP_PRESETS.map((t) => ({ value: t.value, label: t.label, testId: `split-tip-${String(t.value).replace('.', '_')}` }))}
                    className="justify-center"
                    chipClassName="h-10 min-w-[56px] text-[14px]"
                  />
                  {splitTipPreset === 'other' && (
                    <div className="relative max-w-[200px] mx-auto">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft font-medium">£</span>
                      <Input
                        type="number" step="0.01" min="0" placeholder="0.00"
                        value={splitTipOther}
                        onChange={(e) => { setSplitTipOther(e.target.value); onTipChange?.(Math.max(0, parseFloat(e.target.value) || 0)); }}
                        className="pl-8 text-lg font-bold text-center h-11"
                        data-testid="split-tip-other-input"
                      />
                    </div>
                  )}
                  {tipAmount > 0 && (
                    <div className="flex justify-between text-sm text-ink-soft" data-testid="split-tip-amount">
                      <span>Tip</span><span className="tabular-nums">{formatCurrency(tipAmount)}</span>
                    </div>
                  )}
                </div>
              )}
              {cash && splitToTake > 0 && (
                <CashTendered due={splitToTake} value={tendered} onChange={setTendered} formatCurrency={formatCurrency} />
              )}
            </>
          )}
          
          {/* Processing State */}
          {paymentStep === 'processing' && (
            <>
              <div className="w-20 h-20 mx-auto bg-verlo-tint flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-verlo border-t-transparent rounded-full plan-round animate-spin" />
              </div>
              <p className="text-lg font-medium">Recording payment…</p>
              <p className="text-muted-foreground">Please wait</p>
            </>
          )}
          
          {/* Success State */}
          {paymentStep === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto bg-signal-ok-tint rounded-2xl flex items-center justify-center">
                <CheckCircle size={40} className="text-signal-ok" />
              </div>
              {/* What happened, in the till's own voice: the amount and how it was paid. */}
              <p className="font-heading text-2xl font-extrabold tabular-nums" data-testid="paid-headline">
                Paid · {formatCurrency(lastPaid ?? 0)}
              </p>
              {lastChange > 0 && (
                <div className="flex items-center justify-between border border-signal-ok-line bg-signal-ok-tint px-4 py-3 text-signal-ok" data-testid="paid-change">
                  <span className="font-heading text-base font-bold">Give change</span>
                  <span className="font-heading text-3xl font-extrabold tabular-nums">{formatCurrency(lastChange)}</span>
                </div>
              )}
              <p className="text-muted-foreground">
                {[tableLabel, paymentMethod === 'cash' ? 'cash' : 'card', lastTip > 0 ? `${formatCurrency(lastTip)} tip` : null]
                  .filter(Boolean).join(' · ')}
              </p>
              
              {remainingBalance > 0 && (
                <Notice tone="stop" word="Owed" title={<span className="font-mono text-2xl tabular-nums">{formatCurrency(remainingBalance)}</span>}>
                  still to pay on this table
                </Notice>
              )}

              {/* Manager refund escape hatch (only on fully-paid orders that still exist server-side) */}
              {remainingBalance <= 0 && orderId && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRefundOpen(true)}
                  className="border-signal-stop-line text-signal-stop hover:bg-signal-stop-tint gap-1.5 w-full mt-2"
                  data-testid="issue-refund-btn"
                >
                  <ShieldAlert size={13} />
                  Issue refund (manager PIN)
                </Button>
              )}
            </>
          )}
        </div>

        {/* Refund modal — mounted here so it inherits the same dialog stack context. */}
        <RefundDialog
          open={refundOpen}
          onOpenChange={setRefundOpen}
          order={order || { id: orderId, total, refund_amount: 0 }}
          onRefunded={(refunded) => { onRefunded?.(refunded); setRefundOpen(false); }}
          formatCurrency={formatCurrency}
        />
        
        <DialogFooter className="flex-col sm:flex-col gap-2">
          {paymentStep === 'waiting' && !splitBillMode && stage === 'method' && (
            <>
              <Button className="w-full h-11 bg-verlo hover:bg-verlo-hover" onClick={() => setStage('tip')} data-testid="payment-continue-btn">
                Continue
              </Button>
              <Button variant="outline" className="w-full" onClick={() => onSplitBillModeChange(true)} data-testid="split-bill-btn">
                Split the bill
              </Button>
            </>
          )}
          {paymentStep === 'waiting' && !splitBillMode && stage === 'tip' && (
            <>
              <Button className="w-full h-11 bg-verlo hover:bg-verlo-hover" onClick={() => setStage('confirm')} data-testid="payment-continue-btn">
                Continue · {formatCurrency(toTake)}
              </Button>
              <Button variant="outline" className="w-full" onClick={() => setStage('method')}>Back</Button>
            </>
          )}
          {paymentStep === 'waiting' && !splitBillMode && stage === 'confirm' && (
            <>
              <Button className="w-full h-11 bg-verlo hover:bg-verlo-hover" onClick={handleFullPayment} disabled={cashShort(toTake)} data-testid="confirm-payment-btn">
                {cash ? (tendered == null ? 'Enter what they handed over' : cashWords(toTake)) : 'Confirm payment taken'}
              </Button>
              <Button variant="outline" className="w-full" onClick={() => setStage('tip')}>Back</Button>
            </>
          )}
          {paymentStep === 'waiting' && splitBillMode && (
            <>
              <Button 
                className="w-full btn-primary"
                onClick={handlePartialPayment}
                disabled={(splitType === 'amount' && (!customAmount || parseFloat(customAmount) <= 0 || parseFloat(customAmount) > (remainingBalance > 0 ? remainingBalance : total))) || cashShort(splitToTake)}
                data-testid="take-partial-payment-btn"
              >
                {cash ? (tendered == null ? 'Enter what they handed over' : cashWords(splitToTake)) : `Take ${formatCurrency(splitToTake)}`}
              </Button>
              <Button 
                variant="outline" 
                className="w-full" 
                onClick={() => onSplitBillModeChange(false)}
              >
                Pay the whole bill
              </Button>
            </>
          )}
          {paymentStep === 'success' && (
            <>
              {remainingBalance > 0 ? (
                <>
                  <Button 
                    className="w-full btn-primary" 
                    onClick={() => {
                      onPaymentStepChange('waiting');
                      onSplitBillModeChange(true);
                    }}
                    data-testid="take-another-payment-btn"
                  >
                    Take the next payment
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => {
                      onOpenChange(false);
                      onPaymentStepChange('waiting');
                      onSplitBillModeChange(false);
                    }}
                    data-testid="close-partial-payment-btn"
                  >
                    Close
                  </Button>
                </>
              ) : (
                <>
                  {/* Email Receipt Section */}
                  {!receiptSent ? (
                    <div className="w-full space-y-2">
                      <div className="flex gap-2">
                        <Input
                          type="email"
                          placeholder="Guest email for receipt"
                          value={receiptEmail}
                          onChange={(e) => setReceiptEmail(e.target.value)}
                          className="flex-1"
                          data-testid="receipt-email-input"
                        />
                        <Button
                          variant="outline"
                          onClick={handleSendReceipt}
                          disabled={sendingReceipt}
                          data-testid="send-receipt-btn"
                        >
                          <Mail size={14} className="mr-1" />
                          {sendingReceipt ? 'Sending...' : 'Send'}
                        </Button>
                      </div>
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={handlePrintReceipt}
                        disabled={printing}
                        data-testid="print-receipt-btn"
                      >
                        <Printer size={14} className="mr-1.5" />
                        {printing ? 'Printing…' : 'Print receipt'}
                      </Button>
                    </div>
                  ) : (
                    // Queued on the till and sent by the cloud, so "on its way": with the internet
                    // down it waits, and goes when the till is back.
                    <p className="text-sm text-signal-ok w-full text-center">Receipt on its way to {receiptEmail}</p>
                  )}
                  {/* The next thing a server does with a paid table is clear it, so that is the
                      button. Without a table (a takeaway) it is just Done. */}
                  <Button
                    className="w-full btn-primary"
                    onClick={onClearTable || onViewReceipt}
                    data-testid="view-receipt-btn"
                  >
                    {onClearTable && tableLabel ? `Clear ${tableLabel}` : 'Done'}
                  </Button>
                </>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentDialog;
